import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NodeSelection } from "@tiptap/pm/state";

/**
 * Fixed-position box + corner handle when an image is selected — drag to resize inside the editor.
 */
const ImageResizeOverlay = ({ editor, tick }) => {
  const [box, setBox] = useState(null);

  const measure = useCallback(() => {
    if (!editor || editor.isDestroyed) {
      setBox(null);
      return;
    }
    const sel = editor.state.selection;
    if (!(sel instanceof NodeSelection) || sel.node.type.name !== "image") {
      setBox(null);
      return;
    }
    let dom = editor.view.nodeDOM(sel.from);
    if (!dom || dom.nodeName !== "IMG") {
      dom = editor.view.dom.querySelector("img.ProseMirror-selectednode");
    }
    if (!dom || dom.nodeName !== "IMG") {
      setBox(null);
      return;
    }
    const r = dom.getBoundingClientRect();
    setBox({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  }, [editor, tick]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!editor) return;
    let raf = null;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        measure();
      });
    };
    editor.on("transaction", schedule);
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    return () => {
      editor.off("transaction", schedule);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [editor, measure]);

  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!editor) return;

      const sel = editor.state.selection;
      if (!(sel instanceof NodeSelection) || sel.node.type.name !== "image") return;
      let dom = editor.view.nodeDOM(sel.from);
      if (!dom || dom.nodeName !== "IMG") {
        dom = editor.view.dom.querySelector("img.ProseMirror-selectednode");
      }
      if (!dom || dom.nodeName !== "IMG") return;

      const img = dom;
      const startX = e.clientX;
      const startW = img.getBoundingClientRect().width;
      const maxW = Math.max(80, editor.view.dom.getBoundingClientRect().width);

      const onMove = (ev) => {
        if (!editor) return;
        const dx = ev.clientX - startX;
        const newW = Math.max(40, Math.min(maxW, startW + dx));
        editor.chain().focus().updateAttributes("image", { width: `${Math.round(newW)}px` }).run();
      };
      const onUp = () => {
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      document.body.style.userSelect = "none";
      document.body.style.cursor = "nwse-resize";
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [editor]
  );

  if (!box || typeof document === "undefined") return null;

  const handleTop = box.top + box.height - 7;
  const handleLeft = box.left + box.width - 7;

  return createPortal(
    <>
      <div
        aria-hidden
        className="pointer-events-none"
        style={{
          position: "fixed",
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
          zIndex: 10000,
          boxSizing: "border-box",
          border: "2px dashed rgb(6 182 212)",
          borderRadius: 6,
          boxShadow: "0 0 0 1px rgba(255,255,255,0.6) inset",
        }}
      />
      <button
        type="button"
        title="Drag to resize"
        className="fixed w-3.5 h-3.5 rounded-sm bg-cyan-500 border-2 border-white shadow-md hover:bg-cyan-400 z-[10001] cursor-nwse-resize p-0"
        style={{ top: handleTop, left: handleLeft }}
        onMouseDown={startResize}
      />
    </>,
    document.body
  );
};

export default ImageResizeOverlay;
